import { useWatch } from 'react-hook-form';
import { Button } from '@givebutter/primitives/button';
import { DateValue } from '@givebutter/primitives/date-picker';
import { maskDecimal, unmaskNumber } from '@givebutter/util';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PledgeFormValue } from 'assets/js/pages/pledges/usePledgeForm';
import * as Form from 'components/form';
import * as Input from 'components/input';
import * as Tooltip from 'components/tooltip';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';

interface InstallmentItemProps {
    index: number;
    onAmountChange: (value: string, index: number) => void;
    onDateChange?: (date?: DateValue) => void;
    onRemove: (index: number) => void;
    onAppend: () => void;
    canRemove?: boolean;
    disabled?: boolean;
}

const AddInstallment = ({ onAppend }: Pick<InstallmentItemProps, 'onAppend'>) => {
    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <Button
                    variant="transparent"
                    shape={'square'}
                    onClick={onAppend}
                    aria-label="Add installment"
                >
                    <FontAwesomeIcon icon={faPlus} />
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content inverse>
                    <Tooltip.Arrow inverse />
                    <span className="text-white">Add installment</span>
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
};

const RemoveInstallment = ({ onRemove }: { onRemove: () => void }) => {
    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <Button
                    variant="transparent"
                    shape={'square'}
                    onClick={onRemove}
                    aria-label="Remove installment"
                >
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content inverse>
                    <Tooltip.Arrow inverse />
                    <span className="text-white">Remove installment</span>
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
};

const InstallmentItem = ({
    index,
    onAmountChange,
    onDateChange,
    onRemove,
    onAppend,
    canRemove,
    disabled,
}: InstallmentItemProps) => {
    const { control, setValue } = Form.useGivebutterFormContext<PledgeFormValue>();
    const totalPledge = useWatch({ name: 'amount' });
    const total = unmaskNumber(totalPledge || '0');
    const item = useWatch({ control, name: `installments.${index}` });
    const currency = useCurrencySymbol();

    return disabled ? (
        <div className="flex max-w-[348px] gap-4">
            <Form.Container>
                <Form.Label name={`installment.${index}.date`}>Installment {index + 1}</Form.Label>
                <Input.Root>
                    <Input.Form
                        name={`installment.${index}.date`}
                        value={item?.date ? item.date.toFormat('LLL dd, yyyy') : ''}
                        disabled
                    />
                </Input.Root>
            </Form.Container>
            <Form.Container>
                <Form.Label name={`installment.${index}.amount`}>Amount</Form.Label>
                <Input.Root>
                    <Input.Form
                        name={`installment.${index}.amount`}
                        value={item?.amount ? `${currency}${maskDecimal(item.amount)}` : ''}
                        disabled
                    />
                </Input.Root>
            </Form.Container>
        </div>
    ) : (
        <div className="flex items-center gap-4">
            <Form.Container>
                <Form.Label name={`installment.${index}.date`}>Installment {index + 1}</Form.Label>
                <Form.UseFormDatePicker
                    control={control}
                    className="w-full"
                    name={`installments.${index}.date`}
                    onChange={onDateChange}
                />
            </Form.Container>
            <Form.Container>
                <Form.Label name={`installments.${index}.amount`}>Amount</Form.Label>
                <Form.TextInputRoot
                    control={control}
                    name={`installments.${index}.amount`}
                    rules={{
                        validate: {
                            max: value =>
                                unmaskNumber(value) <= total ||
                                'Amount must be less than total pledge.',
                        },
                    }}
                    inputRootProps={{
                        mask: 'decimal',
                    }}
                >
                    <Form.TextInputForm
                        onKeyUp={() => {
                            setValue(`installments.${index}.override`, true);
                        }}
                        onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                            onAmountChange((event.target as HTMLInputElement).value, index)
                        }
                    />
                </Form.TextInputRoot>
                <Form.Error name={`installments.${index}.amount`} />
            </Form.Container>
            <div className="mt-5 flex">
                {canRemove && <RemoveInstallment onRemove={() => onRemove(index)} />}
                <AddInstallment onAppend={onAppend} />
            </div>
        </div>
    );
};

export { InstallmentItem };
